import React, { useState } from 'react';
import { Button, Divider, Form, Input, InputNumber, Modal, Row, Typography, message } from 'antd';
import { LeftOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { each, isEmpty } from 'lodash';
import ErrorBoundary from 'components/Errors/ErrorBoundary';
import BackIcon from 'components/UtilComponents/BackIcon';
import ModalCloseIcon from 'components/UtilComponents/ModalCloseIcon';
import { formatQuantity, formatRefNo } from 'utils/formatter';
import { DISPATCH_URL } from 'constants/ApiConstants';
import { useEffect } from 'react';
import { checkForAnomaly } from 'utils/anomaly';

const { Paragraph, Title, Text } = Typography;

const EditDispatch = ({ order, visible, close, mode, selected }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState('collect-details');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected) {
      form.resetFields();
    } else {
      setTimeout(() => {
        form.setFields([
          { name: 'order', value: formatRefNo(selected?.order?.orderRefNo) },
          { name: 'purchaseBillNo', value: selected?.purchaseBillNo },
          { name: 'dispatchedQty', value: selected?.dispatchedQty },
          { name: 'vehicleNo', value: selected?.vehicleNo },
          { name: 'dispatchFrom', value: selected?.dispatchFrom },
          { name: 'notes', value: selected?.notes },
        ]);
      }, 1);
    }
  }, [selected]);

  const totalQty = order?.qty;
  const deliveredQty = order?.metadata?.dispatchedQty || 0;
  const remainingQty = totalQty - deliveredQty > 0 ? totalQty - deliveredQty : 0;

  const onClickPrev = () => {
    if (step === 'review-details') return setStep('collect-details');
  };

  const onClickNext = () => {
    if (step === 'collect-details') return setStep('review-details');
    if (step === 'review-details') return onSubmit();
  };

  const getTitle = () => {
    if (step === 'collect-details') {
      return <Title level={5}>Edit Dispatch</Title>;
    }
    return (
      <>
        <BackIcon onClick={onClickPrev} className="mr-3" />
        {' Review Dispatch Details'}
      </>
    );
  };

  const onSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const values = form.getFieldsValue(true);
      const detectedAnomaly = checkForAnomaly('dispatch', { ...values, subUnit: order?.subUnit, type: 'Purchase' });
      if (detectedAnomaly) {
        setLoading(false);
        return message.error(detectedAnomaly);
      }
      const res = await axios.put(`${DISPATCH_URL}/${selected?._id}`, {
        purchaseBillNo: values.purchaseBillNo,
        dispatchedQty: values.dispatchedQty,
        dispatchedOn: new Date(),
        dispatchFrom: values.dispatchFrom,
        vehicleNo: values.vehicleNo,
        notes: values.notes,
        transitStatus: selected?.transitStatus,
      });

      if (res.data.status === 'success') {
        message.success('Dispatch details updated successfully');

        // upload related files for created dispatch
        const files = new FormData();

        if (!isEmpty(values?.invoice)) {
          files.append('purchase.invoice', values?.invoice[0].originFileObj);
          if (values?.purchaseInvoiceTime) {
            files.append('purchase.invoice_time', values?.purchaseInvoiceTime);
          }
        }
        if (!isEmpty(values?.ewayBill)) {
          files.append('purchase.ewayBill', values?.ewayBill[0].originFileObj);
          if (values?.purchaseEwayBillTime) {
            files.append('purchase.ewayBill_time', values?.purchaseEwayBillTime);
          }
        }
        if (!isEmpty(values?.weightSlip)) {
          files.append('purchase.weightSlip', values?.weightSlip[0].originFileObj);
          if (values?.purchaseWeightSlipTime) {
            files.append('purchase.weightSlip_time', values?.purchaseWeightSlipTime);
          }
        }
        if (!isEmpty(values?.qualityReport)) {
          files.append('purchase.qualityReport', values?.qualityReport[0].originFileObj);
          if (values?.purchaseQualityReportTime) {
            files.append('purchase.qualityReport_time', values?.purchaseQualityReportTime);
          }
        }
        if (!isEmpty(values?.attachments)) {
          each(values.attachments, (attachment) => {
            files.append('purchase.attachments', attachment.originFileObj);
          });
          if (values?.purchaseAttachmentsTime) {
            files.append('purchase.attachments_time', values?.purchaseAttachmentsTime);
          }
        }

        await axios.post(`${DISPATCH_URL}/upload/${selected?._id}`, files);

        setStep('collect-details');
        form.resetFields();
        close(true);
      } else {
        message.error(res.data.error);
      }
    } catch (err) {
      message.error(err.message);
    }
    setLoading(false);
  };

  const confirmOnClose = () => {
    if ((!loading && form.isFieldsTouched()) || step === 'review-details') {
      Modal.confirm({
        title: 'Unsaved Data',
        icon: <ExclamationCircleOutlined />,
        content: 'This form has unsaved data. Are you sure you want to close the screen?',
        okText: 'Yes',
        onOk: () => {
          form.resetFields();
          setStep('collect-details');
          close(false);
        },
        cancelText: 'No',
      });
    } else {
      setStep('collect-details');
      close(false);
    }
  };

  return (
    <Modal title={getTitle()} open={visible} onCancel={confirmOnClose} footer={null} closeIcon={<ModalCloseIcon />} maskClosable={false} width={480} bodyStyle={{ height: '75vh', overflow: 'scroll' }}>
      <ErrorBoundary>
        <Form form={form} layout="vertical" onFinish={step === 'collect-details' ? onClickNext : onSubmit} requiredMark={false} scrollToFirstError={true}>
          {step === 'collect-details' && (
            <>
              {mode == 'create-dispatch' && (
                <Form.Item
                  name="order"
                  valuePropName="value"
                  label="Order No"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a valid Order No',
                    },
                  ]}>
                  <Input size="large" disabled style={{ marginTop: '8px' }} />
                </Form.Item>
              )}
              <Form.Item
                name="purchaseBillNo"
                label="Invoice / E-way Bill No"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the invoice no',
                  },
                ]}
                hasFeedback>
                <Input maxLength={25} size="large" />
              </Form.Item>

              <Form.Item
                name="dispatchedQty"
                label={`Dispatched Quantity (${selected?.subUnit})`}
                className="bg-transparent"
                rules={[
                  {
                    required: true,
                    message: 'Please enter valid dispatched quantity',
                  },
                  {
                    validator: (_, value) => {
                      if (value <= remainingQty * 1.25) {
                        return Promise.resolve();
                      }
                      return Promise.reject(`Dispatched Quantity should be lesser than ${formatQuantity(remainingQty * 1.25)} ${order?.subUnit ? `(${order?.subUnit})` : ''}`);
                    },
                  },
                ]}
                hasFeedback>
                <InputNumber precision={3} className="w-100" size="large" min={1} controls={false} />
              </Form.Item>

              <Form.Item
                name="vehicleNo"
                label="Vehicle No"
                className="bg-transparent"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the vehicle no',
                  },
                ]}
                hasFeedback>
                <Input size="large" maxLength={15} />
              </Form.Item>

              <Form.Item
                name="dispatchFrom"
                label="Dispatch Area Pincode"
                className="bg-transparent"
                rules={[
                  {
                    required: true,
                    pattern: /^[0-9]*$/,
                    message: 'Please enter the Dispatch From Pincode',
                  },
                  () => ({
                    validator(_, value) {
                      if (value.toString()?.length > 0) {
                        if (value.toString()?.length !== 6) {
                          return Promise.reject('Please enter a valid 6 digit Pin Code');
                        }
                        return Promise.resolve();
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                hasFeedback>
                <Input size="large" maxLength={12} />
              </Form.Item>

              <Form.Item label="Additional Information" className="bg-transparent" name="notes">
                <Input.TextArea size="large" rows={3} maxLength={300} showCount allowClear />
              </Form.Item>
            </>
          )}

          {/* Review the details before submitting */}
          {step === 'review-details' && (
            <>
              <>
                <Row justify="space-between" className="mt-3">
                  <Text>Invoice No:</Text>
                  <Text>{form.getFieldValue('purchaseBillNo')}</Text>
                </Row>

                <Row justify="space-between" className="my-1">
                  <Text>Dispatched Quantity:</Text>
                  <Text>
                    {formatQuantity(form.getFieldValue('dispatchedQty'))} {order?.subUnit}
                  </Text>
                </Row>

                {form.getFieldValue('vehicleNo') && (
                  <Row justify="space-between" className="my-1">
                    <Text>Vehicle No:</Text>
                    <Text>{form.getFieldValue('vehicleNo')}</Text>
                  </Row>
                )}
                {form.getFieldValue('dispatchFrom') && (
                  <Row justify="space-between" className="my-1">
                    <Text>Dispatch Area Pincode:</Text>
                    <Text>{form.getFieldValue('dispatchFrom')}</Text>
                  </Row>
                )}
              </>

              <Divider />

              {form.getFieldValue('notes') && (
                <>
                  <Paragraph>
                    <strong>Additional Information:</strong>
                  </Paragraph>
                  <Paragraph>&bull; {form.getFieldValue('notes')}</Paragraph>
                  <Divider />
                </>
              )}

              <Form.Item className="mt-4">
                <Button type="link" onClick={() => setStep('collect-details')} icon={<LeftOutlined />}>
                  Edit Details
                </Button>
              </Form.Item>
            </>
          )}

          <Form.Item className="mt-4">
            <Button type="primary" size="large" block htmlType="submit" loading={loading}>
              {step === 'collect-details' && 'Next'}
              {step === 'review-details' && 'Edit Dispatch'}
            </Button>
          </Form.Item>
        </Form>
      </ErrorBoundary>
    </Modal>
  );
};

export default EditDispatch;
